import { getOpportunityStatusColor } from './opportunityStatusOptions.js';

const MENU_PORTAL_TARGET =
  typeof document !== 'undefined' ? document.body : null;

/** Shared react-select props for modal dropdowns (portal + fixed menu). */
export const MODAL_SELECT_PROPS = {
  menuPortalTarget: MENU_PORTAL_TARGET,
  menuPosition: 'fixed',
};

/**
 * Base modal select styles — company, grant, assignee (non-status).
 */
export function buildOpportunityModalSelectStyles({ menuMinWidth } = {}) {
  return {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      borderColor: state.isFocused ? '#111827' : '#e5e7eb',
      borderRadius: '0.5rem',
      boxShadow: state.isFocused ? '0 0 0 1px #111827' : 'none',
      fontSize: '0.875rem',
      '&:hover': { borderColor: '#111827' },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontSize: '0.6875rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isSelected ? '#111827' : isFocused ? '#f3f4f6' : undefined,
      color: isSelected ? '#ffffff' : '#111827',
      whiteSpace: 'nowrap',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      ...(menuMinWidth
        ? { minWidth: `${menuMinWidth}px`, width: 'max-content' }
        : {}),
    }),
    menuList: (provided) => ({
      ...provided,
      ...(menuMinWidth ? { minWidth: `${menuMinWidth}px` } : {}),
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    singleValue: (provided) => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
  };
}

/** Color-coded single-select styles for opportunity status. */
export function buildOpportunityStatusSelectStyles() {
  const base = buildOpportunityModalSelectStyles();
  return {
    ...base,
    groupHeading: (provided) => ({
      ...provided,
      fontSize: '0.6875rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      paddingTop: '10px',
      paddingBottom: '4px',
      marginBottom: 0,
    }),
    option: (styles, { data, isFocused, isSelected }) => {
      const color = data.color || getOpportunityStatusColor(data.value);
      return {
        ...styles,
        backgroundColor: isSelected
          ? `${color}25`
          : isFocused
            ? '#f3f4f6'
            : undefined,
        color: isSelected ? color : '#111827',
        fontWeight: isSelected ? 600 : 400,
        paddingLeft: '20px',
      };
    },
    singleValue: (provided, { data }) => ({
      ...provided,
      color: getOpportunityStatusColor(data?.value),
      fontWeight: 600,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
  };
}

const statusOptionStyle = (styles, { data, isFocused, isSelected }, { compact = false } = {}) => {
  const color = data.color || getOpportunityStatusColor(data.value);
  return {
    ...styles,
    backgroundColor: isSelected
      ? `${color}25`
      : isFocused
        ? '#f3f4f6'
        : undefined,
    color: isSelected ? color : '#111827',
    fontWeight: isSelected ? 600 : 400,
    paddingLeft: compact ? '12px' : '20px',
    ...(compact ? { fontSize: '11px', padding: '4px 8px' } : {}),
  };
};

/** Multi-select status filter — grouped, color-coded chips (All Opportunities board). */
export function buildOpportunityStatusFilterSelectStyles() {
  const base = buildOpportunityStatusSelectStyles();
  return {
    ...base,
    control: (provided, state) => ({
      ...base.control(provided, state),
      minHeight: '38px',
      height: 'auto',
    }),
    valueContainer: (provided) => ({
      ...provided,
      maxHeight: 'none',
      minHeight: '36px',
      overflowY: 'auto',
      overflowX: 'hidden',
      flexWrap: 'wrap',
      padding: '4px 8px',
      gap: '4px',
    }),
    option: (styles, state) => statusOptionStyle(styles, state),
    multiValue: (styles, { data }) => {
      const color = data.color || getOpportunityStatusColor(data.value);
      return {
        ...styles,
        backgroundColor: `${color}22`,
        borderRadius: '0.375rem',
        maxWidth: '100%',
      };
    },
    multiValueLabel: (styles, { data }) => {
      const color = data.color || getOpportunityStatusColor(data.value);
      return {
        ...styles,
        color,
        fontWeight: 600,
        fontSize: '0.75rem',
        whiteSpace: 'normal',
        lineHeight: '1.25',
      };
    },
    multiValueRemove: (styles, { data }) => {
      const color = data.color || getOpportunityStatusColor(data.value);
      return {
        ...styles,
        color,
        ':hover': {
          backgroundColor: color,
          color: 'white',
        },
      };
    },
  };
}

/** Compact inline status select in the opportunities table. */
export function buildOpportunityStatusInlineSelectStyles() {
  const base = buildOpportunityStatusSelectStyles();
  return {
    ...base,
    control: (provided, state) => ({
      ...base.control(provided, state),
      minHeight: '28px',
      fontSize: '11px',
      height: 'auto',
      minWidth: '180px',
      maxWidth: '200px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0px 6px',
      minHeight: '28px',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
      padding: '0px',
      fontSize: '11px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '28px',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '240px',
      fontSize: '11px',
    }),
    option: (styles, state) => statusOptionStyle(styles, state, { compact: true }),
    singleValue: (provided, { data }) => ({
      ...provided,
      color: getOpportunityStatusColor(data?.value),
      fontWeight: 600,
      fontSize: '11px',
      whiteSpace: 'normal',
      lineHeight: '1.25',
    }),
  };
}

/** Color-coded template picker that sits in the PerfectDraft compose row. */
export function buildPerfectDraftTemplateSelectStyles() {
  const base = buildOpportunityStatusSelectStyles();
  return {
    ...base,
    control: (provided) => ({
      ...provided,
      minHeight: '42px',
      border: 'none',
      borderRadius: 0,
      boxShadow: 'none',
      backgroundColor: 'transparent',
      fontSize: '0.875rem',
      cursor: 'pointer',
      '&:hover': { border: 'none', boxShadow: 'none' },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px 0 0',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '42px',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '8px',
      color: '#9ca3af',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    option: (styles, { data, isFocused, isSelected }) => {
      const color = data.color || '#475569';
      return {
        ...styles,
        backgroundColor: isSelected
          ? `${color}25`
          : isFocused
            ? '#f3f4f6'
            : undefined,
        color: isSelected ? color : '#111827',
        fontWeight: isSelected ? 600 : 400,
        paddingLeft: '20px',
      };
    },
    singleValue: (provided, { data }) => ({
      ...provided,
      color: data?.color || '#111827',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      minWidth: '280px',
      width: 'max-content',
      maxWidth: '28rem',
    }),
  };
}
